import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Loader, UnstyledButton, Input, InputWrapperProps } from '@any-ui-react/core';
import { DropzoneOptions, useDropzone, fromEvent, FileRejection } from '@any-ui-react/dropzone';
import classNames from 'classnames';
import { z } from 'zod';

import { CloseIcon, FileTextIcon } from '@anyx/shared-icons';
import { reportAppError } from '@anyx/shared-utils';

import {
  formatBytes,
  formatDropzoneAccept,
  getVideoDuration,
  isIdenticalSize,
  isImageFile,
  isVideoFile,
} from './utils';

const UploadFileState = z.union([z.literal('local'), z.literal('remote'), z.literal('error')]);
export type UploadFileState = z.infer<typeof UploadFileState>;

export const UploadedImageFile = z.object({
  type: z.literal('image'),
  src: z.string(),
  name: z.string(),
  state: UploadFileState,
  file: z.instanceof(File).optional(),
});

export type UploadedImageFile = z.infer<typeof UploadedImageFile>;

export const UploadedVideoFile = z.object({
  type: z.literal('video'),
  src: z.string(),
  name: z.string(),
  state: UploadFileState,
  file: z.instanceof(File).optional(),
  duration: z.number().optional(),
  thumbnailSrc: z.string().optional(), // Optional thumbnail URL for preview
  thumbnailFile: z.instanceof(File).optional(), // Optional thumbnail File for upload
  width: z.number().optional(), // Video width
  height: z.number().optional(), // Video height
});

export type UploadedVideoFile = z.infer<typeof UploadedVideoFile>;

export const UploadedDocumentFile = z.object({
  type: z.literal('document'),
  src: z.string(),
  name: z.string(),
  state: UploadFileState,
  file: z.instanceof(File).optional(),
  fileSize: z.number().optional(),
  mimeType: z.string().optional(),
});

export type UploadedDocumentFile = z.infer<typeof UploadedDocumentFile>;

export type UploadedFile = UploadedImageFile | UploadedVideoFile | UploadedDocumentFile;

interface DroppedFile extends File {
  width?: number;
  height?: number;
  duration?: number;
}

export type DimensionsType = {
  width: number;
  height: number;
};

export type MediaUploadProps = Omit<
  DropzoneOptions,
  'onDropAccepted' | 'onDropRejected' | 'validator'
> & {
  /** MIME types to block (e.g. ['video/mp4']) */
  blockedMimeTypes?: string[];
  /** Maximum video file size in bytes */
  maxVideoSize?: number;
  /** Maximum document file size in bytes */
  maxDocumentSize?: number;
  /** Maximum Dimension of Image Upload */
  maxDimensions?: DimensionsType;
  /** Minimum Dimension of Image Upload */
  minDimensions?: DimensionsType;
  /** Maximum Duration of Video Upload (in seconds) */
  maxDuration?: number;
  /** Callback when files rejected */
  onFilesReject?: (fileRejections: FileRejection[]) => void;
  /** Callback when files added */
  onFilesAdded?: (files: DroppedFile[]) => void;
  /** Callback when files removed */
  onFileRemove?: (file: UploadedFile) => void;
  /** Callback when retry file upload */
  onFileErrorRetry?: (file: UploadedFile, index: number) => void;
  /** Uploaded Files */
  files?: UploadedFile[];
  inputWrapperProps?: Omit<InputWrapperProps, 'children'>;
};

const Preview = ({
  onClick,
  onRetry,
  disabled,
  file,
}: {
  onClick?: () => void;
  onRetry?: () => void;
  disabled?: boolean;
  file: UploadedFile;
}) => {
  const { src, name, state, type } = file;

  return (
    <div className="relative z-10 flex w-[120px] flex-col gap-y-1">
      {!disabled && (
        <UnstyledButton
          className="bg-gray-9 hover:bg-gray-2 absolute -top-2 -right-2 z-10 flex h-4 w-4 items-center rounded-full p-1 text-white"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClick?.();
          }}
        >
          <CloseIcon />
        </UnstyledButton>
      )}
      <div className="relative">
        {state === 'local' && (
          <div className="bg-gray-0/[0.5] absolute right-0 left-0 flex h-full w-full items-center justify-center">
            <Loader size="sm" />
          </div>
        )}
        {state === 'error' && file.file && onRetry && (
          <div className="bg-gray-0/[0.3] absolute right-0 left-0 flex h-full w-full items-center justify-center">
            <UnstyledButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRetry?.();
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="fill-gray-9 h-10 w-10"
              >
                <g>
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path d="M7 20.981a6.5 6.5 0 0 1-2.936-12 8.001 8.001 0 0 1 15.872 0 6.5 6.5 0 0 1-2.936 12V21H7v-.019zM13 13h3l-4-5-4 5h3v4h2v-4z" />
                </g>
              </svg>
            </UnstyledButton>
          </div>
        )}
        {type === 'image' && (
          <img
            src={src}
            alt={name}
            className={classNames(
              'aspect-square rounded-sm border bg-white object-cover object-center',
              {
                'border-gray-2': state !== 'error',
                'border-failure': state === 'error',
              }
            )}
          />
        )}
        {type === 'video' && (
          <video
            src={src}
            poster={file.thumbnailSrc}
            aria-label={name}
            title={name}
            className={classNames(
              'aspect-square rounded-sm border bg-black object-cover object-center',
              {
                'border-gray-2': state !== 'error',
                'border-failure': state === 'error',
              }
            )}
            preload="metadata"
            muted
          />
        )}
        {type === 'document' && (
          <div
            className={classNames(
              'bg-gray-1 flex aspect-square items-center justify-center rounded-sm border',
              {
                'border-gray-2': state !== 'error',
                'border-failure': state === 'error',
              }
            )}
          >
            <FileTextIcon className="text-gray-6 h-10 w-10" />
          </div>
        )}
      </div>
      <span className="text-gray-9 text-2xs truncate">{name}</span>
    </div>
  );
};

const SizeLabel = ({
  dimensions,
  size,
}: {
  dimensions: DimensionsType;
  size: 'MIN' | 'MAX' | 'SAME';
}) => {
  const { t } = useTranslation('crm');

  return (
    <p className={size === 'SAME' ? 'font-semibold' : ''}>
      {t('crm.mediaUpload.restrictions.dimensions', {
        size,
        width: dimensions.width,
        height: dimensions.height,
      })}
    </p>
  );
};

export function AnyChatMediaUpload({
  accept,
  blockedMimeTypes = [],
  maxFiles = 0,
  maxSize,
  maxVideoSize,
  maxDocumentSize,
  maxDimensions,
  minDimensions,
  maxDuration,
  onFilesReject,
  onFilesAdded,
  onFileRemove,
  onFileErrorRetry,
  disabled = false,
  files = [],
  inputWrapperProps,
}: MediaUploadProps) {
  const { t } = useTranslation('crm');

  const numOfFiles = files.length;
  const isExceedMaxNumberOfFiles = maxFiles !== 0 && numOfFiles >= maxFiles;

  const onDropAccepted = useCallback<NonNullable<DropzoneOptions['onDropAccepted']>>(
    (acceptedFiles) => {
      onFilesAdded?.(acceptedFiles);
    },
    [onFilesAdded]
  );

  const onDropRejected = useCallback<NonNullable<DropzoneOptions['onDropRejected']>>(
    (fileRejections) => {
      onFilesReject?.(fileRejections);
    },
    [onFilesReject]
  );

  /**
   * Due to maxSize is defined when render the dropzone, but we need to decide it based on the file type when validate the file
   * So validate the file size on "validator" prop, don't pass maxSize here.
   */
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    disabled,
    onDropAccepted,
    onDropRejected,
    accept,
    maxFiles,
    multiple: maxFiles === 0 || maxFiles > 1,
    noClick: isExceedMaxNumberOfFiles,
    getFilesFromEvent: async (event) => {
      const fromEventFiles = await fromEvent(event); // reuse the same logic from react-dropzone

      // Add custom fields to the file object
      return Promise.all(
        fromEventFiles.map(async (fromEventFile) => {
          if (fromEventFile instanceof DataTransferItem) {
            return fromEventFile;
          }

          if (isImageFile(fromEventFile)) {
            return new Promise((resolve) => {
              const url = URL.createObjectURL(fromEventFile);
              let width;
              let height;
              const img = new Image();

              img.onload = function () {
                width = img.width;
                height = img.height;

                resolve(
                  Object.assign(fromEventFile, {
                    width,
                    height,
                  }) as DroppedFile
                );
              };

              img.src = url;
            });
          }

          if (isVideoFile(fromEventFile)) {
            try {
              const duration = await getVideoDuration(fromEventFile);
              return Object.assign(fromEventFile, {
                duration,
              }) as DroppedFile;
            } catch (error) {
              reportAppError(error);
              return fromEventFile;
            }
          }

          return fromEventFile;
        })
      );
    },
    validator: (droppedFile: DroppedFile) => {
      // Check if file type is blocked
      if (blockedMimeTypes.length > 0 && blockedMimeTypes.includes(droppedFile.type)) {
        return {
          code: 'file-type-blocked',
          message: t('crm.mediaUpload.error.fileTypeNotSupported'),
        };
      }

      // Check file size based on file type
      const getCurrentMaxSize = () => {
        if (isVideoFile(droppedFile)) return maxVideoSize;
        if (isImageFile(droppedFile)) return maxSize;
        // All other file types (PDF, Excel, txt, doc, etc.) use document size limit
        return maxDocumentSize;
      };
      const currentMaxSize = getCurrentMaxSize();

      if (currentMaxSize && droppedFile.size > currentMaxSize) {
        return {
          code: 'file-too-large',
          message: t('crm.mediaUpload.restrictions.maxFileSize', {
            maxFileSize: formatBytes(currentMaxSize),
          }),
        };
      }

      if (maxDimensions && isImageFile(droppedFile)) {
        const { width = 0, height = 0 } = droppedFile;
        const { width: maxWidth, height: maxHeight } = maxDimensions;

        if (width > maxWidth || height > maxHeight) {
          return {
            code: 'image-dimension-exceed',
            message: t('crm.mediaUpload.error.dimensionExceeded', { maxWidth, maxHeight }),
          };
        }
      }

      if (minDimensions && isImageFile(droppedFile)) {
        const { width = 0, height = 0 } = droppedFile;
        const { width: minWidth, height: minHeight } = minDimensions;

        if (width < minWidth || height < minHeight) {
          return {
            code: 'smaller-image-dimension',
            message: t('crm.mediaUpload.error.belowMinimum', { minWidth, minHeight }),
          };
        }
      }

      if (maxDuration && isVideoFile(droppedFile)) {
        const { duration = 0 } = droppedFile;

        if (duration > maxDuration) {
          return {
            code: 'video-duration-exceed',
            message: t('crm.mediaUpload.error.durationExceeded', {
              maxDuration,
            }),
          };
        }
      }

      return null;
    },
  });

  // When accept is undefined or empty object, all file types are accepted
  const acceptAllFiles = !accept || Object.keys(accept).length === 0;
  const acceptImageTypes = accept
    ? Object.fromEntries(Object.entries(accept).filter(([key]) => key.startsWith('image/')))
    : {};
  const acceptVideoTypes = accept
    ? Object.fromEntries(Object.entries(accept).filter(([key]) => key.startsWith('video/')))
    : {};
  const acceptDocumentTypes = accept
    ? Object.fromEntries(Object.entries(accept).filter(([key]) => key.startsWith('application/')))
    : {};

  const imageRestrictionsNodes = [
    ...(maxSize
      ? [
          <span>
            {t('crm.mediaUpload.restrictions.max', {
              maxFileSize: formatBytes(maxSize),
            })}
          </span>,
        ]
      : []),
    ...(acceptImageTypes ? [<span>({formatDropzoneAccept(acceptImageTypes)})</span>] : []),
  ];

  const videoRestrictionsNodes = [
    ...(maxVideoSize
      ? [
          <span>
            {t('crm.mediaUpload.restrictions.max', {
              maxFileSize: formatBytes(maxVideoSize),
            })}
          </span>,
        ]
      : []),
    ...(maxDuration
      ? [
          <span>
            {t('crm.mediaUpload.restrictions.duration', {
              duration: maxDuration,
            })}
          </span>,
        ]
      : []),
    ...(acceptVideoTypes ? [<span>({formatDropzoneAccept(acceptVideoTypes)})</span>] : []),
  ];

  const documentRestrictionsNodes = [
    ...(maxDocumentSize
      ? [
          <span>
            {t('crm.mediaUpload.restrictions.max', {
              maxFileSize: formatBytes(maxDocumentSize),
            })}
          </span>,
        ]
      : []),
    // Only show file extensions when specific document types are defined
    ...(Object.keys(acceptDocumentTypes).length > 0
      ? [<span>({formatDropzoneAccept(acceptDocumentTypes)})</span>]
      : []),
  ];

  // Enable video upload UI only if accept prop includes video mime types
  const enableVideoUpload = Object.keys(acceptVideoTypes).length > 0;
  // Enable document upload UI if accept prop includes document mime types OR accepts all files
  const enableDocumentUpload = Object.keys(acceptDocumentTypes).length > 0 || acceptAllFiles;

  return (
    <Input.Wrapper
      {...inputWrapperProps}
      classNames={{
        error: 'mt-1',
      }}
    >
      <div
        {...getRootProps({
          className: classNames(
            'bg-gray-0 flex h-auto min-h-fit w-full flex-wrap gap-x-3 gap-y-3 border-2 p-4',
            {
              'border-gray-2': !isDragActive,
              'border-blue-6': isDragActive && !isExceedMaxNumberOfFiles,
              'flex-col items-center justify-center': numOfFiles === 0,
              'cursor-default! border-solid': isExceedMaxNumberOfFiles,
              'border-dashed': !isExceedMaxNumberOfFiles,
              'border-failure!': !!inputWrapperProps?.error,
            }
          ),
        })}
      >
        <input {...getInputProps()} />
        {numOfFiles === 0 ? (
          <>
            <Button disabled={disabled} variant="default">
              {t('crm.mediaUpload.uploadFile')}
            </Button>
            <p className="text-gray-7 text-sm">{t('crm.mediaUpload.dropDesc')}</p>
            {(enableVideoUpload || enableDocumentUpload) && (
              <div className="text-gray-7 text-3xs text-center">
                {acceptAllFiles ? (
                  // When accepting all files, show Image, File, and Video size limits
                  <>
                    {maxSize && (
                      <p className="flex items-center justify-center gap-x-1">
                        <span>{t('crm.mediaUpload.fileTypes.image', { ns: 'crm' })}:</span>
                        <span>
                          {t('crm.mediaUpload.restrictions.max', {
                            maxFileSize: formatBytes(maxSize),
                          })}
                        </span>
                        <span>(.jpeg, .jpg, .png)</span>
                      </p>
                    )}
                    {maxDocumentSize && (
                      <p className="flex items-center justify-center gap-x-1">
                        <span>{t('crm.mediaUpload.fileTypes.file')}:</span>
                        <span>
                          {t('crm.mediaUpload.restrictions.max', {
                            maxFileSize: formatBytes(maxDocumentSize),
                          })}
                        </span>
                      </p>
                    )}
                    {!!maxVideoSize && (
                      <p className="flex items-center justify-center gap-x-1">
                        <span>{t('crm.mediaUpload.fileTypes.video')}:</span>
                        <span>
                          {t('crm.mediaUpload.restrictions.max', {
                            maxFileSize: formatBytes(maxVideoSize),
                          })}
                        </span>
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="flex items-center justify-center gap-x-1">
                      <span>{t('crm.mediaUpload.fileTypes.image', { ns: 'crm' })}:</span>
                      {imageRestrictionsNodes.map((node, index) => (
                        <span key={index}>
                          {index > 0 && ', '}
                          {node}
                        </span>
                      ))}
                    </p>
                    {enableDocumentUpload && (
                      <p className="flex items-center justify-center gap-x-1">
                        <span>{t('crm.mediaUpload.fileTypes.file')}:</span>
                        {documentRestrictionsNodes.map((node, index) => (
                          <span key={index}>
                            {index > 0 && ', '}
                            {node}
                          </span>
                        ))}
                      </p>
                    )}
                    {enableVideoUpload && (
                      <p className="flex items-center justify-center gap-x-1">
                        <span>{t('crm.mediaUpload.fileTypes.video')}:</span>
                        {videoRestrictionsNodes.map((node, index) => (
                          <span key={index}>
                            {index > 0 && ', '}
                            {node}
                          </span>
                        ))}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
            {!enableVideoUpload && !enableDocumentUpload && (
              <div className="text-gray-7 text-3xs text-center">
                {maxSize && (
                  <p>
                    {t('crm.mediaUpload.restrictions.maxFileSize', {
                      maxFileSize: formatBytes(maxSize),
                    })}
                  </p>
                )}
                {accept && (
                  <p>
                    {t('crm.mediaUpload.restrictions.supportedFiles', {
                      supportedFiles: formatDropzoneAccept(acceptImageTypes),
                    })}
                  </p>
                )}
                {maxDimensions && isIdenticalSize(maxDimensions, minDimensions) ? (
                  <SizeLabel dimensions={maxDimensions} size="SAME" />
                ) : (
                  <>
                    {maxDimensions && <SizeLabel dimensions={maxDimensions} size="MAX" />}
                    {minDimensions && <SizeLabel dimensions={minDimensions} size="MIN" />}
                  </>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {files.map((file, i) => {
              return (
                <Preview
                  key={i}
                  disabled={disabled}
                  onClick={() => {
                    onFileRemove?.(file);
                  }}
                  onRetry={() => onFileErrorRetry?.(file, i)}
                  file={file}
                />
              );
            })}
            {!isExceedMaxNumberOfFiles && (
              <div className="text-info text-2xs flex h-[120px] w-[120px] items-center justify-center border-2 border-dashed">
                {t('crm.mediaUpload.addFile')}
              </div>
            )}
          </>
        )}
      </div>
    </Input.Wrapper>
  );
}
